import { SidebarCollection } from "@core/components/SideBarCollection";
import { SidebarCollections } from "@core/components/SidebarCollections";
import { IconCTA } from "@core/components/cta/IconCTA";
import { PrimaryCTA } from "@core/components/cta/PrimaryCTA";
import { StIcon } from "@core/components/icons";
import { getEditorExtensions, promptEditorExtensions } from "@core/domains/chat/prompt/extensions/getExtensions";
import { getPromptTextAndDecorations } from "@core/domains/chat/prompt/getPromptTextAndDecorations";
import { transformPastedHTML } from "@core/domains/chat/prompt/pasteTransform";
import { transformPastedText } from "@core/domains/chat/prompt/pasteTransform";
import { getRootCollectionUIOverloads } from "@core/domains/collections/collections.helpers";
import { CollectionKind } from "@core/domains/collections/collections.types";
import { useWire } from "@core/wire";
import Dialog from "corvu/dialog";
import Drawer from "corvu/drawer";
import { BsPersonFillLock } from "solid-icons/bs";
import { IoClose } from "solid-icons/io";
import { TbChevronRight, TbLock } from "solid-icons/tb";
import { type Component, For, Show, createSignal } from "solid-js";
import { twMerge } from "tailwind-merge";

export const SavePromptCollectionPickerModal: Component<{
  open: boolean;
  onCollectionSelected?: (id: string) => void;
  setOpen: (open: boolean) => void;
  collectionId?: string;
  triggerClass?: string;
}> = (props) => {
  const wire = useWire();

  const active = () => wire.services.collections.getCollection(props.collectionId || "");
  const override = () => getRootCollectionUIOverloads(active());
  const [search, setSearch] = createSignal("");

  const isPersonalLibrary = () =>
    props.collectionId === wire.services.collections.getRootCollectionByKind(CollectionKind.Personal)?.id;

  return (
    <>
      <Dialog open={props.open} onOpenChange={props.setOpen} trapFocus={false}>
        <div>
          <p class="mb-2">Save to</p>
          <Dialog.Trigger
            class={twMerge(
              "inline-flex items-center gap-2 w-auto bg-surface border border-solid border-surface-decoration rounded-lg py-0.5 px-3",
              props.triggerClass,
            )}
          >
            <Show
              when={isPersonalLibrary()}
              fallback={
                <>
                  <StIcon icon={override()?.icon!} />
                  <span>{override()?.label}</span>
                  <StIcon icon={TbChevronRight} />
                </>
              }
            >
              <StIcon icon={BsPersonFillLock} />
              <span>My Personal Library</span>
              <StIcon icon={TbChevronRight} />
            </Show>
          </Dialog.Trigger>
        </div>

        <Dialog.Portal>
          <Dialog.Overlay class="fixed inset-0 z-max bg-black/50 data-[open]:animate-fade-in data-[closed]:animate-fade-out transition-opacity" />
          <Dialog.Content
            class={`text-on-background fixed left-1/2 top-1/2 z-max -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background px-6 py-5 w-full max-w-[50rem]
            data-[open]:animate-dialog-open data-[closed]:animate-dialog-close transition-opacity origin-top`}
          >
            <div class="">
              <div class="flex justify-between items-start gap-2 mb-4">
                <div class="mt-3">
                  <Dialog.Label class="type-title-sm">
                    Save to your personal library or a shared Collection
                  </Dialog.Label>
                  <p class="type-body-sm text-on-background-variant mb-6">
                    Saving the prompt to a shared Collection will allow others with access to that Collection to see the
                    prompt and use it in their own chats.
                  </p>
                </div>

                <Dialog.Close
                  as={IconCTA}
                  icon={IoClose}
                  modifiers={["small"]}
                  accessibleLabel="Close the dialog"
                  data-test-id="dialog-close"
                  class="w-auto"
                />
              </div>

              <div>
                <button
                  type="button"
                  class="w-full rounded-lg px-3 py-2 mb-4 hover:bg-surface-hover"
                  classList={{
                    "bg-surface-decoration hover:bg-surface-decoration":
                      props.collectionId ===
                      wire.services.collections.getRootCollectionByKind(CollectionKind.Personal)?.id,
                  }}
                  onClick={() => {
                    props.onCollectionSelected?.(
                      wire.services.collections.getRootCollectionByKind(CollectionKind.Personal)?.id || "",
                    );
                  }}
                >
                  <span>My Personal Library</span>
                </button>
              </div>

              <div>
                <input
                  class="bg-background w-full rounded-lg px-3 py-2 mb-4 border border-solid border-surface-decoration outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Search for a Collection"
                  value={search()}
                  onInput={(e) => setSearch(e.currentTarget.value)}
                />
              </div>

              <div class="px-3 flex-auto overflow-y-auto max-h-[calc(100vh_-_15rem)]">
                <SidebarCollections
                  filter={search()}
                  filterRoots={[CollectionKind.Org]}
                  activeCollectionId={props.collectionId}
                  onCollectionClick={(id) => {
                    const isShared = id === wire.services.collections.getRootCollectionByKind(CollectionKind.Org)?.id;
                    if (isShared) return;
                    props.onCollectionSelected?.(id);
                  }}
                />
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </>
  );
};
